import {useFlags} from 'launchdarkly-react-client-sdk';
import React from 'react';
import {v4 as uuid} from 'uuid';
import {Box, Stack, styled} from '@mui/material';
import {designSystemToken} from '@lightricks/react-design-system';
import UserMenu from '@contentlab/components/user-menu';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import {media} from '@contentlab/styles/mediaQueries';
import {CONTENT_LAB_VIEW_NAMES} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import {DashboardUser} from '@/types/dashboardUser';
import {UserMenuItem} from '../user-menu/userMenuItems';
import MenuItem, {MenuItemProps} from './MenuItem';
import {getMenuItemsList} from './menuItemsList';

interface MenuProps {
  testID?: string;
  user: DashboardUser;
  open: boolean;
  isMobile: boolean;
  onToggle: () => void;
}

const contentLabIcon = '/assets/svg/icon-content-lab.svg';
const contentLabLogo = '/assets/svg/logo-content-lab.svg';

const CUBIC_BEZIER = 'cubic-bezier(0.4, 0, 0.2, 1)';
const MENU_WIDTH_OPEN = 227;
const MENU_WIDTH_CLOSED = 67;

const MenuContainer = styled(Box)<{open: boolean}>(({open}) => ({
  width: open ? MENU_WIDTH_OPEN : MENU_WIDTH_CLOSED,
  padding: open ? '16px 16px 24px 16px' : '16px 8px 24px 8px',
  transition: `width 0.3s ${CUBIC_BEZIER}, padding 0.3s ${CUBIC_BEZIER}`,
  background: designSystemToken('semantic.bg.tertiary'),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-between',
  boxSizing: 'border-box',
  gap: '8px',

  [media.mobile]: {
    width: '100%',
    overflowX: 'auto',
    padding: '16px 8px',
  },
}));

const MenuHeader = styled(Box)(() => ({
  width: '100%',
  height: '44px',
  display: 'flex',
  gap: '4px',
  alignItems: 'center',

  [media.mobile]: {
    display: 'none',
  },
}));

const CollapseIconContainer = styled(Box)(() => ({
  'display': 'flex',
  'justifyContent': 'center',
  'alignItems': 'center',
  'padding': '10px 14px',
  'borderRadius': '8px',
  'cursor': 'pointer',
  ':hover': {
    background: designSystemToken('semantic.bg.neutral-inverse'),
  },
}));

const ContentLabIcon = styled('img')<{open: boolean}>(({open}) => ({
  transform: open ? 'unset' : 'translateX(50%)',
  transition: `transform 0.2s ${CUBIC_BEZIER}`,
}));

const MenuItemsContainer = styled(Stack)(() => ({
  boxSizing: 'border-box',
  flex: 1,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  transition: `padding 0.3s ${CUBIC_BEZIER}`,
  gap: '8px',

  [media.mobile]: {
    flexDirection: 'row',
  },
}));

const MenuFooter = styled(Box)(() => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  [media.mobile]: {
    display: 'none',
  },
}));

function Menu({testID = 'menu', user, open, isMobile, onToggle}: MenuProps) {
  const {uiInteraction} = useUiInteractionEvent();
  const flags = useFlags();

  const menuItems = getMenuItemsList(flags);

  const handleToggleMenu = () => {
    const nextOpen = !open;
    uiInteraction({
      item_name: `menu_toggle: ${nextOpen ? 'open' : 'close'}`,
      interaction_location: CONTENT_LAB_VIEW_NAMES.MENU,
      interaction_id: uuid(),
    });
    onToggle();
  };

  const onMenuItemClick = (menuItem: MenuItemProps) => {
    uiInteraction({
      item_name: `menu_item: ${menuItem.label}`,
      interaction_location: CONTENT_LAB_VIEW_NAMES.MENU,
      interaction_id: uuid(),
    });
  };

  const onUserMenuOpen = () => {
    uiInteraction({
      item_name: `user_menu: open`,
      interaction_location: CONTENT_LAB_VIEW_NAMES.USER_MENU,
      interaction_id: uuid(),
    });
  };

  const onUserMenuClose = () => {
    uiInteraction({
      item_name: `user_menu_item: close`,
      interaction_location: CONTENT_LAB_VIEW_NAMES.USER_MENU,
      interaction_id: uuid(),
    });
  };

  const onUserMenuItemClick = (userMenuItem: UserMenuItem) => {
    uiInteraction({
      item_name: `user_menu_item: ${translate(userMenuItem.labelLocaleKey)}`,
      interaction_location: CONTENT_LAB_VIEW_NAMES.USER_MENU,
      interaction_id: uuid(),
    });
  };

  return (
    <MenuContainer data-testid={testID} open={open}>
      <MenuHeader>
        <CollapseIconContainer onClick={handleToggleMenu}>
          <img key={contentLabLogo} src={contentLabLogo} alt="Ltx Scaler" />
        </CollapseIconContainer>
        <ContentLabIcon open={open} src={contentLabIcon} alt="Ltx Scaler" />
      </MenuHeader>
      <MenuItemsContainer sx={{alignItems: 'center', flex: 1}}>
        {menuItems.map((item) => (
          <MenuItem
            key={item.id}
            testID={`menu-item--${item.id}`}
            iconName={item.iconName}
            label={item.label}
            to={item.path}
            open={open}
            isMobile={isMobile}
            onClick={onMenuItemClick}
            matchPaths={item.matchPaths}
            disabled={item.disabled}
            tooltip={item.tooltip}
          />
        ))}
      </MenuItemsContainer>
      <MenuFooter>
        <UserMenu
          user={user}
          open={open}
          onOpen={onUserMenuOpen}
          onClose={onUserMenuClose}
          onItemClick={onUserMenuItemClick}
        />
      </MenuFooter>
    </MenuContainer>
  );
}

export default Menu;
